<template>
  <div class="onboarding-container">
    <div class="onboarding-content">
      <div class="setup-hero">
        <div class="hero-icon">
          <i class="fa-solid fa-magic-wand-sparkles"></i>
        </div>
        <h1 class="setup-title">Let's Set Up Your Budget</h1>
        <p class="setup-subtitle">Describe your finances in plain English and let AI do the work</p>
      </div>

      <div class="setup-card">
        <div class="card-header">
          <div class="header-icon">
            <i class="fa-solid fa-robot"></i>
          </div>
          <div class="header-content">
            <h3 class="card-title">AI-Powered Setup</h3>
            <p class="card-subtitle">Get started in seconds with natural language budget creation</p>
          </div>
        </div>
        <div class="card-body">
          <p class="setup-description">
            Simply tell us about your monthly income and expenses in your own words.
            Our AI will understand and set up your budget automatically.
          </p>
          <div class="example-prompts">
            <p class="example-label">
              <i class="fa-solid fa-lightbulb me-2"></i>
              Example prompts:
            </p>
            <div class="prompt-item">
              <i class="fa-solid fa-quote-left"></i>
              <span>I make $3000 a month and my rent is $1200</span>
            </div>
            <div class="prompt-item">
              <i class="fa-solid fa-quote-left"></i>
              <span>My income is $5000, rent $1500, utilities $200, groceries $400</span>
            </div>
            <div class="prompt-item">
              <i class="fa-solid fa-quote-left"></i>
              <span>I earn $4500 monthly with $2000 in fixed expenses</span>
            </div>
          </div>
          <button @click="$emit('start-natural-setup')" class="btn btn-primary btn-lg setup-button">
            <i class="fa-solid fa-rocket me-2"></i>
            Start AI Setup
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'OnboardingFlow',
  emits: ['start-natural-setup']
}
</script>

<style scoped>
.onboarding-container {
  position: fixed;
  top: 80px;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--light-bg);
  padding: 2rem;
  overflow-y: auto;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .onboarding-container {
    top: 120px;
  }
}

.onboarding-content {
  max-width: 700px;
  width: 100%;
  animation: fadeInUp 0.6s ease-out;
}

.setup-hero {
  text-align: center;
  margin-bottom: 2rem;
}

.hero-icon {
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #36A2EB, #4BC0C0);
  border-radius: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  box-shadow: 0 10px 40px rgba(54, 162, 235, 0.3);
  animation: float 3s ease-in-out infinite;
}

.hero-icon i {
  font-size: 2.5rem;
  color: white;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.setup-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--light-text);
  margin-bottom: 0.75rem;
}

.setup-subtitle {
  font-size: 1.2rem;
  color: var(--light-text-muted);
  margin: 0;
}

.setup-card {
  background: var(--nested-container-bg);
  border-radius: 8px;
  padding: 1rem;
  transition: all 0.2s ease;
  border: 2px dashed var(--light-border);
}

.setup-card .card-header {
  background: var(--light-card);
  border-bottom: none;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 0;
  border-radius: 8px;
}

.setup-card .header-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--light-text);
  font-size: 1.25rem;
  flex-shrink: 0;
  border: 1px solid var(--light-text);
}

.setup-card .header-content {
  flex: 1;
  text-align: left;
}

.setup-card .card-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--light-text);
  margin-bottom: 0.25rem;
}

.setup-card .card-subtitle {
  font-size: 0.95rem;
  color: var(--light-text-muted);
  margin: 0;
}

.setup-card .card-body {
  padding: 1.5rem;
  background: var(--light-card);
  border-radius: 8px;
  margin-top: 1rem;
}

.setup-description {
  font-size: 1rem;
  color: var(--light-text);
  line-height: 1.6;
  margin-bottom: 2rem;
  text-align: center;
}

.example-prompts {
  background: var(--nested-container-bg);
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border: 2px dashed var(--light-border);
}

.example-label {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--light-text);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
}

.example-label i {
  color: #36A2EB;
}

.prompt-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  background: var(--light-card);
  border-radius: 8px;
  margin-bottom: 0.75rem;
  font-size: 0.95rem;
  color: var(--light-text-muted);
  line-height: 1.5;
}

.prompt-item:last-child {
  margin-bottom: 0;
}

.prompt-item i {
  color: #36A2EB;
  font-size: 0.75rem;
  margin-top: 0.25rem;
  flex-shrink: 0;
}

.setup-button {
  width: 100%;
  background-color: #36A2EB;
  border-color: #36A2EB;
  min-height: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.setup-button:hover {
  background-color: #2e8bc7;
  border-color: #2e8bc7;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(54, 162, 235, 0.3);
}

.setup-button:active {
  transform: translateY(0);
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Dark Mode Support */
:global([data-theme]) .hero-icon {
  box-shadow: 0 10px 40px rgba(54, 162, 235, 0.4);
}

:global([data-theme]) .setup-card {
  background: var(--nested-container-bg);
  border-color: var(--light-border);
}

:global([data-theme]) .setup-card .card-header,
:global([data-theme]) .setup-card .card-body {
  background: var(--light-card);
}

:global([data-theme]) .example-prompts {
  background: var(--nested-container-bg);
  border-color: var(--light-border);
}

:global([data-theme]) .prompt-item {
  background: var(--light-card);
}

:global([data-theme]) .setup-button:hover {
  box-shadow: 0 8px 25px rgba(54, 162, 235, 0.4);
}

/* Responsive Design */
@media (max-width: 768px) {
  .onboarding-container {
    top: 120px;
    padding: 1rem;
  }

  .onboarding-content {
    max-width: 100%;
  }

  .hero-icon {
    width: 80px;
    height: 80px;
    border-radius: 20px;
  }

  .hero-icon i {
    font-size: 2rem;
  }

  .setup-title {
    font-size: 2rem;
  }

  .setup-subtitle {
    font-size: 1.1rem;
  }

  .setup-card .card-header {
    padding: 1.25rem;
  }

  .setup-card .card-body {
    padding: 1.25rem;
  }

  .setup-description {
    font-size: 0.95rem;
  }

  .example-prompts {
    padding: 1.25rem;
  }

  .prompt-item {
    font-size: 0.9rem;
  }

  .setup-button {
    font-size: 1rem;
  }
}

@media (max-width: 576px) {
  .setup-title {
    font-size: 1.75rem;
  }

  .setup-subtitle {
    font-size: 1rem;
  }

  .setup-card .header-icon {
    width: 45px;
    height: 45px;
    font-size: 1.1rem;
  }

  .setup-card .card-title {
    font-size: 1.1rem;
  }

  .setup-card .card-subtitle {
    font-size: 0.875rem;
  }
}
</style>